import { _ } from "libs";

import React, { useEffect, useState } from "react";
import withRedux from "../../withRedux";
import Modal from "../modal";
import {
	closeChallengeLeaderboardPopup,
	closePlayerPopup,
	getChallengeLeaderboard,
	openPlayerPopup,
	showChallengeLeaderboardModal,
} from "../../actions";
import { RootState } from "../../reducers";
import { Championship, LeaderboardData, Player, User } from "../../types";
import { Row } from "./leaderboard";
import { InvisibleInfiniteLoader } from "../infiniteLoader";

function ChallengeLeaderboardModal({
	show = false,
	championship = null,
	challengeId = null,
	modalTitle = null,
	onPlayerSelected = null,
	showChallengeLeaderboardModal,
}: {
	show: boolean;
	championship: Championship;
	challengeId: string;
	modalTitle?: string;
	onPlayerSelected?: any;
	showChallengeLeaderboardModal;
}) {
	const [leaderboards, setLeaderboards] = useState([] as LeaderboardData[]);
	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [selectedUser, selectUser] = useState(null as User);
	const [currentPage, setCurrentPage] = useState(0 as number);
	const [playerName, setPlayerName] = useState(null as string);

	const PAGE_SIZE = 40;

	useEffect(() => {
		if (!championship || !challengeId) {
			return;
		}
		setLoading(true);
		getChallengeLeaderboard(championship.uuid, challengeId, currentPage, PAGE_SIZE).then((res) => {
			if (!res) {
				setLoading(false);
				return;
			}
			const { data, pageRows, pageCurrent } = res;
			if (data.length < PAGE_SIZE) {
				setHasMore(false);
			} else {
				setHasMore(true);
			}
			setLeaderboards([...leaderboards, ...data]);
			setLoading(false);
			return () => {
				setLeaderboards([]);
			};
		});
	}, [championship, currentPage, challengeId, playerName]);

	if (show === false) {
		return null;
	}

	const close = () => {
		closeChallengeLeaderboardPopup();
		setHasMore(true);
		setLeaderboards([]);
		setPlayerName(null);
		setCurrentPage(0);
	};

	const onViewChangeDown = async (inView: boolean, observerEntry: IntersectionObserverEntry) => {
		if (!championship) {
			return;
		}
		const shouldLoad = inView && leaderboards.length > 0 && hasMore;

		if (shouldLoad) {
			setCurrentPage(currentPage + 1);
		}
	};

	const getOnPlayerSelected = (player: Player, championship?: Championship) => {
		return () => {
			close();
			openPlayerPopup(
				player.playerId,
				true,
				{ championshipName: championship.name },
				{
					championship,
					leagueId: player.leagueId,
					backAction: () => {
						closePlayerPopup();
						showChallengeLeaderboardModal(challengeId, championship, onPlayerSelected, modalTitle);
					},
				}
			);
		};
	};

	return (
		<Modal title={modalTitle} largeTitle={true} fullHeight={true} onCloseClicked={close}>
			<div className="table players -mx-3">
				{leaderboards.map((row: LeaderboardData, key: number) => {
					return (
						<Row
							key={`${row.player.userId}_${key}_${row.rank.rank}`}
							player={row.player}
							rank={row.rank}
							position={row.rank.rank}
							championship={championship}
							index={key}
							onClick={() => {
								getOnPlayerSelected(row.player, championship)();
							}}
							selected={selectedUser ? row.player.userId === selectedUser.userId : false}
						/>
					);
				})}
				{!loading && leaderboards.length <= 0 && (
					<div className="emptyScreen">
						<p className="hint">{_("navbarSearchAutocomplete.noResults")}</p>
					</div>
				)}
				{hasMore ? <InvisibleInfiniteLoader onViewChange={onViewChangeDown} loading={loading} /> : null}
			</div>
		</Modal>
	);
}

function mapStateToProps({
	challengeLeaderboardModal: { show, championship, challengeId, modalTitle, onPlayerSelected },
}: RootState) {
	return {
		show,
		championship,
		challengeId,
		modalTitle,
		onPlayerSelected,
	};
}

export default withRedux({ showChallengeLeaderboardModal }, mapStateToProps)(ChallengeLeaderboardModal);
